/**
 * ペースト正規化ロジック
 *
 * ProseMirror のペーストは「文字列段(transformPastedHTML) → パース → slice段(transformPasted)」の順。
 * 掃除は前者、YouTube 埋め込みは text/html 両経路が合流する後者に置く
 *
 * @tiptap/pm は型でも実行時でも import しない。直接依存に宣言しておらず strict pnpm で解決できないため。
 * ProseMirror 値へは渡された引数の構造とコンストラクタ経由で触る
 */
import { generateYoutubeThumbnail } from '@/service/daily/generateYoutubeThumbnail';
import { sanitizeRichTextHtml } from '@/util/html/sanitizeRichTextHtml';

/** transformPasted が参照するノードの最小構造 */
interface PastedNode {
  isTextblock: boolean;
  textContent: string;
}
/** transformPasted が参照する Slice の最小構造。Fragment は iterable でないので添字で読む */
interface PastedFragment {
  childCount: number;
  child(index: number): PastedNode;
}
interface PastedSlice {
  content: PastedFragment;
}
/** buildYoutubeNodes / transformPasted が参照する schema・view の最小構造 */
interface EditorSchema {
  nodes: {
    iframe?: { create(attrs: Record<string, string>): unknown };
    paragraph?: { create(attrs: null, content: unknown): unknown };
  };
  marks: { link?: { create(attrs: Record<string, string>): unknown } };
  text(text: string, marks: unknown[]): unknown;
}
interface PasteEditorView {
  state: { schema: EditorSchema };
}

const MODAL_OUTSIDE_SELECTORS = ['modal-backdrop'];
const YOUTUBE_ANCHOR_TEXT = /^https:\/\/(?:www|m)\.youtube\.com\//;

/**
 * モーダル外 DOM (.modal-backdrop 等) が混ざっていれば代替テキストを返す (無ければ null)
 * 混入要素は取り除いてからテキスト化する。背景の文言まで本文として貼られてしまうため
 */
const modalOutsideFallbackText = (html: string): string | null => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const offenders = MODAL_OUTSIDE_SELECTORS.flatMap((selector) => Array.from(doc.querySelectorAll('.' + selector)));
  if (offenders.length === 0) return null;
  for (const el of offenders) el.remove();
  return doc.body.textContent ?? '';
};

/** テキストを HTML に埋め込む前のエスケープ */
const escapeHtml = (text: string): string => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * URL から YouTube 埋め込みノード列 (iframe + URL リンク行) を作る (埋め込めなければ null)
 * 生成 HTML から属性を取り出して schema のノードを直接作る = @tiptap/pm を import しないため
 */
const buildYoutubeNodes = (schema: EditorSchema, url: string): unknown[] | null => {
  const iframeType = schema.nodes.iframe;
  if (!iframeType) return null;
  // 文字列補間だと " 入りの URL が href の属性境界を壊す
  const sourceAnchor = document.createElement('a');
  sourceAnchor.setAttribute('href', url);
  sourceAnchor.textContent = url;
  const embedHtml = sanitizeRichTextHtml(generateYoutubeThumbnail(sourceAnchor.outerHTML));
  const embedDoc = new DOMParser().parseFromString(embedHtml, 'text/html');
  const iframe = embedDoc.querySelector('iframe');
  if (!iframe) return null;
  const attrs: Record<string, string> = {};
  // tsconfig の lib に DOM.Iterable が無いので DOM コレクションは Array.from で回す
  for (const attr of Array.from(iframe.attributes)) {
    attrs[attr.name] = attr.value;
  }
  const nodes: unknown[] = [iframeType.create(attrs)];
  const urlLine = buildYoutubeUrlLine(schema, embedDoc.querySelector('a'));
  if (urlLine) nodes.push(urlLine);
  return nodes;
};

/**
 * 埋め込みの下に出す「URL: ...」リンク行を paragraph ノードにする (作れなければ null)
 * link mark に href 以外を付けると autolink が <a>URL: </a><a>https://...</a> へ貼り直し、
 * 後者を blur 変換が同じ動画としてもう一度埋め込んでしまう
 */
const buildYoutubeUrlLine = (schema: EditorSchema, anchor: HTMLAnchorElement | null): unknown => {
  const paragraphType = schema.nodes.paragraph;
  const linkType = schema.marks.link;
  if (!anchor || !paragraphType || !linkType) return null;
  const text = anchor.textContent;
  const href = anchor.getAttribute('href');
  if (!text || !href) return null;
  return paragraphType.create(null, schema.text(text, [linkType.create({ href })]));
};

/**
 * Windows CF_HTML のラッパー境界の空白を除去する
 * Windows のクリップボードは本文を html/body/フラグメントで包み境界に \r\n を入れる。ProseMirror 由来
 * (data-pm-slice 付き) はこの \r\n が上下の空段落として残るため Mac 相当に揃える
 */
export const stripPastedWrapperWhitespace = (html: string): string => {
  if (!html.includes('data-pm-slice')) return html;
  return html
    .replace(/(<\/?(?:html|head|body)[^>]*>)\s+/gi, '$1')
    .replace(/>\s+(<!--(?:Start|End)Fragment-->)/gi, '>$1')
    .replace(/(<!--(?:Start|End)Fragment-->)\s+/gi, '$1');
};

/** ブロックの中身が単独の YouTube URL か (前後にテキストが無い) */
export const isYoutubeUrlOnly = (text: string): boolean => {
  const trimmed = (text || '').trim();
  return trimmed !== '' && !/\s/.test(trimmed) && YOUTUBE_ANCHOR_TEXT.test(trimmed);
};

/**
 * 「テキストが YouTube URL の <a>」を埋め込み iframe に変換する (blur 時に使用)
 * @returns changed=変換有無, html=結果 HTML
 */
export const convertYoutubeAnchorsInHtml = (html: string): { changed: boolean; html: string } => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let changed = false;
  for (const a of Array.from(doc.querySelectorAll('a'))) {
    if (!YOUTUBE_ANCHOR_TEXT.test(a.textContent || '')) continue;
    const tmp = doc.createElement('div');
    tmp.innerHTML = generateYoutubeThumbnail(a.outerHTML);
    if (tmp.querySelector('iframe')) {
      a.replaceWith(...Array.from(tmp.childNodes));
      changed = true;
    }
  }
  return { changed, html: changed ? doc.body.innerHTML : html };
};

/**
 * [ProseMirror transformPastedHTML] 挿入前の HTML 文字列を正規化する
 * モーダル外DOM混入はテキスト化 (画面破壊防止)、Windows の \r\n を除去、DOMPurify で無害化
 */
export const transformPastedHTML = (html: string): string => {
  const fallbackText = modalOutsideFallbackText(html);
  if (fallbackText !== null) return escapeHtml(fallbackText);
  return sanitizeRichTextHtml(stripPastedWrapperWhitespace(html));
};

/**
 * [ProseMirror transformPasted] text経路(直貼りURL)と html経路(リンク)の合流点
 * 「1ブロック丸ごとが YouTube URL」のブロックだけ埋め込み + URL リンク行にする (インラインは blur 側が拾う)
 */
export const transformPasted = (slice: PastedSlice, view: PasteEditorView): unknown => {
  const { schema } = view.state;
  const { content } = slice;
  const children = Array.from({ length: content.childCount }, (_, index) => content.child(index));
  const nodes: unknown[] = [];
  let changed = false;
  for (const node of children) {
    if (node.isTextblock && isYoutubeUrlOnly(node.textContent)) {
      const embed = buildYoutubeNodes(schema, node.textContent.trim());
      if (embed) {
        nodes.push(...embed);
        changed = true;
        continue;
      }
    }
    nodes.push(node);
  }
  if (!changed) return slice;
  // Slice/Fragment の静的メソッドは受け取った値のコンストラクタ経由で使う (@tiptap/pm を import しないため)
  const sliceCtor = slice.constructor as unknown as { maxOpen(fragment: unknown): unknown };
  const fragmentCtor = slice.content.constructor as unknown as { fromArray(nodes: unknown[]): unknown };
  return sliceCtor.maxOpen(fragmentCtor.fromArray(nodes));
};

/** ペースト系フックの例外はフェイルオープン (握りつぶさずログのみ) */
export const logPasteError = (error: unknown): void => {
  const logger = (
    window as unknown as { browserLogger?: { error(message: string, context: object, err: Error): void } }
  ).browserLogger;
  if (!logger) return;
  logger.error(
    'ペースト処理で予期しないエラーが発生しました',
    {},
    error instanceof Error ? error : new Error(String(error))
  );
};
